/**
 * Duplicate-a-past-job engine — pure-TS port of the monolith's dup subsystem
 * (legacy/smart-email-request/index.html L5213–5335):
 *
 *   - dupSource past-only filter ("PAST job means deployed", 2026-07-22 per Jack)
 *   - dupPrefetch warm cache (boot + every division change) + localStorage
 *     stale-while-revalidate (2026-07-22, per Jack: "modal felt slow")
 *   - dupPickTemplate — infer the nurture template from the job's text (jobs
 *     rows don't carry a `template` field)
 *   - the message-code / sender-type derivation regexes dupApply uses
 *
 * UI lives in components/DupModal.tsx; the field-apply lives in FormView.
 */
import { getJobs } from "./api";
import { echTplStage, stageWord } from "./echTemplates";

/** a /jobs row — loose by contract (the worker adds fields over time) */
export type DupJob = Record<string, unknown>;

/* A STRINGIFIED NULL IS STILL EMPTY (2026-08-28, BAU) ─────────────────────────
   Reported as jobs showing "null" underneath the job name. A real JS `null` was
   already handled; what arrives from /jobs is the STRING "null", produced
   somewhere upstream by a `String(value)` over an absent Airtable cell. Three
   consequences, all of them this one cause: the row printed the word, a truthiness
   test on the raw field passed, and the value was fed into the /sent-email lookup
   as if it were a real subject — which is the same report's empty preview.
   Same shape as the `multipleSelects`-returns-plain-strings trap that cost ~5
   redeploys on /active-jobs.json. Fixed here so every consumer inherits it.
   Upstream still sends it; that is a backend fix and this does not depend on it. */
const NULLISH = /^(null|undefined|nan)$/i;
/** string-coerce a job field ("" for null/undefined and for their string forms) */
export const jstr = (j: DupJob, k: string): string => {
  const v = j[k];
  if (v == null) return "";
  const s = String(v);
  return NULLISH.test(s.trim()) ? "" : s;
};

/** monolith dupSource — drop jobs with a FUTURE deploy date (scheduled ≠ history) */
export function pastOnly(jobs: DupJob[]): DupJob[] {
  const now = Date.now();
  return jobs.filter((j) => {
    if (!j || !j.deploy) return true;
    const t = Date.parse(String(j.deploy));
    return !(t && t > now);
  });
}

/* ── A JOB IS OFFERED AS SOON AS IT IS SUBMITTED (2026-10-29, Jack) ────────────────────
   Resubmit used to list sent jobs only (`pastOnly`), so a job filed today for next week
   could not be copied until next week. Now every submitted job is listed; one that has not
   sent yet carries a "Scheduled" tag, and a cancelled one is never offered (the backend
   drops it too — this covers a list still cached from before). `pastOnly` stays for any
   caller that wants history only. */
/** the job's send date is still ahead */
export function isScheduled(j: DupJob, now = Date.now()): boolean {
  const t = Date.parse(jstr(j, "deploy"));
  return Number.isFinite(t) && t > now;
}
/** every job that is not cancelled, in the order the backend sent */
export function offerable(jobs: DupJob[]): DupJob[] {
  return (jobs || []).filter((j) => j && !/\bcancel/i.test(jstr(j, "status")));
}

/* ── localStorage stale-while-revalidate (monolith dupLS/dupLSPut) ── */
const lsKey = (div: string): string => "dupJobs:" + String(div || "").toUpperCase();
export function dupLS(div: string): DupJob[] | null {
  try {
    const j = JSON.parse(localStorage.getItem(lsKey(div)) || "null");
    return j && Array.isArray(j.jobs) ? (j.jobs as DupJob[]) : null;
  } catch { return null; }
}
export function dupLSPut(div: string, jobs: DupJob[]): void {
  /* THE BLANK-DIVISION KEY IS DELIBERATE, NOT A LEAK (corrected 2026-08-28).
     `dupJobs:` with no division is the list DupModal shows when it is opened before
     a division has been picked — a real path, and the one main's
     FormView.duppastdates.test.tsx seeds. An earlier version of this change dropped
     it as junk and broke that test; the key is normalised, never withheld. */
  try { localStorage.setItem(lsKey(div), JSON.stringify({ t: Date.now(), jobs })); } catch { /* quota */ }
}

/* ── prefetch cache (monolith __dupPre) — warmed at boot and on division change ── */
const pre: Record<string, DupJob[]> = {};
export const dupPre = (div: string): DupJob[] | undefined => pre[div];
export function dupPrefetch(div: string): void {
  try {
    if (pre[div]) return;
    getJobs(div, { app: true }).then((d) => {
      if (d && Array.isArray(d.jobs)) { pre[div] = d.jobs; dupLSPut(div, d.jobs); }
    }).catch(() => {});
  } catch { /* never blocks */ }
}
/** the modal's own live refresh also feeds the cache */
export function dupPrePut(div: string, jobs: DupJob[]): void {
  pre[div] = jobs;
  dupLSPut(div, jobs);
}

/**
 * A JOB JUST FILED HAS TO BE FINDABLE (2026-08-28).
 *
 * The past-jobs list is cached twice — in this module for the life of the page
 * (`pre[div]`, which `dupPrefetch` never invalidates) and in localStorage — and a
 * submit invalidated neither. So the request someone just filed could not be
 * duplicated until the tab was closed, which is the one genuine CACHE bug in the
 * 2026-08-27 feedback batch (the rest were page state).
 *
 * Drops both layers for the division so the next open re-fetches. Deliberately
 * does NOT pre-fetch: the edge caches /jobs for ~5 minutes, so an immediate
 * re-fetch would just re-cache the pre-submit answer. Left to the next open,
 * which is also when it matters.
 */
export function dupInvalidate(div: string): void {
  if (!div) return;
  delete pre[div];
  try { localStorage.removeItem(lsKey(div)); } catch { /* private mode */ }
}

/* ── template inference (monolith dupPickTemplate + TPL_PREVIEW stages) ── */
export const TPL_STAGE: Record<string, string> = {
  "Tease - Coming Soon": "Tease", "Launch - Sales Just Launched": "Launch",
  "Launch - Grand Opening Invite": "Launch", "Sustain - Actively Selling": "Sustain",
  "Tease - Join the VIP List": "Tease", "Fast Track - Tease Coming Soon": "Tease",
  "Tease - GO Exclusive Sneak Peek": "Tease", "Tease - Amenities (Checkerboard)": "Tease",
  "Launch - Grand Opening Thank You": "Launch", "Sustain - Tour Your Way": "Sustain",
  "Sustain - Own Your Dream": "Sustain", "Sustain - LM HomeReady First": "Sustain",
  "Last Chance - Models Now Selling": "LastChance", "Last Chance - NSE Inventory Closeout": "Urgency",
  "Last Chance - Final Opportunity": "LastChance", "Event - Ribbon Cutting": "Events",
  "Event - Celebration Theme": "Events",
};

/* LIVE SFMC TEMPLATE NAMES MAP TO A STAGE TOO (2026-08-19).
   TPL_STAGE above is keyed on the catalog's stage-prefixed names ("Tease - Coming Soon").
   The picker now lists the LIVE SFMC names ("Coming Soon"), which match none of those keys,
   so `TPL_STAGE[tplName] || ""` began submitting an EMPTY templateStage — silently, on every
   send. Resolve by the stage word the name carries, falling back to the exact table first so
   nothing about the catalog path changes. */
const LIVE_STAGE: [RegExp, string][] = [
  [/last chance|final opportunity|final homes|closeout/i, "LastChance"],
  [/ribbon|celebration|webinar|event/i, "Events"],
  /* "We're Open - Schedule a Tour" (2026-10-06) opens with the launch set, like a
     Grand Opening; any apostrophe form */
  [/grand opening|just launched|now selling|thank you|we['’]?re open|now open/i, "Launch"],
  /* "Just Released - Floorplans Now Available" (2026-10-06): Katy's catalog files
     floorplan releases under Tease ("Tease - Floorplans Release (Photo Feature)") */
  [/coming soon|sneak peek|vip list|amenities|just released|floorplans? (now available|release)/i, "Tease"],
];
export function tplStageOf(name: string): string {
  /* THE TEMPLATE SAYS ITS OWN STAGE FIRST (2026-10-06, Layne): a `stage` key on the
     ECH_TPL_ block, else the stage its aliases carry. Only a template that says nothing
     falls through to the name tables below — so a new template needs no code change. */
  const exact = TPL_STAGE[name];          /* the old catalog names keep their exact stage */
  if (exact) return exact;
  const declared = echTplStage(name) || stageWord(name);
  if (declared) return declared;
  const n = String(name || "");
  for (const [re, stage] of LIVE_STAGE) if (re.test(n)) return stage;
  return "Sustain";   /* the monolith's own default arm in dupPickTemplate */
}
const PICK_STOP = ["last", "chance", "fast", "track", "tease", "event", "sustain", "launch"];

export function dupPickTemplate(job: DupJob, templates: string[]): string | null {
  try {
    const txt = (jstr(job, "project") + " " + jstr(job, "initiative") + " " + jstr(job, "subject") + " " + jstr(job, "cat")).toLowerCase();
    let best: string | null = null, bestScore = 0;
    templates.forEach((name) => {
      let sc = 0;
      name.toLowerCase().split(/[^a-z0-9]+/).forEach((w) => {
        if (w.length > 3 && PICK_STOP.indexOf(w) < 0 && txt.indexOf(w) > -1) sc++;
      });
      if (sc > bestScore) { bestScore = sc; best = name; }
    });
    if (best && bestScore >= 1) return best;
    const stage = /last chance|final|closeout/.test(txt) ? "LastChance"
      : /grand opening|now open|launch|thank you/.test(txt) ? "Launch"
      : /coming soon|tease|sneak|vip/.test(txt) ? "Tease"
      : /ribbon|celebration|webinar|event/.test(txt) ? "Events" : "Sustain";
    for (let i = 0; i < templates.length; i++) {
      if (TPL_STAGE[templates[i]] === stage) return templates[i];
    }
  } catch { /* inference must never block the apply */ }
  return null;
}

/** dupApply's message derivation (monolith `_mc` regex chain, L5316) — returns
 *  the Primary Message CODE, exactly what setSelVal($("message"),_mc) sets. */
export function deriveMessage(job: DupJob): string {
  const t2 = (jstr(job, "project") + " " + jstr(job, "initiative") + " " + jstr(job, "subject") + " " + (jstr(job, "category") || jstr(job, "cat"))).toLowerCase();
  return /coming soon|tease|sneak|vip/.test(t2) ? "CS"
    : /grand opening|now open|launch/.test(t2) ? "GO"
    : /last chance|final|closeout/.test(t2) ? "FO"
    : /webinar/.test(t2) ? "Web"
    : /ribbon|celebration|special event/.test(t2) ? "SEvt"
    : /active adult|55\+/.test(t2) ? "55" : "";
}

/** resolve a /jobs row's `message` (a code on new rows, a label on older ones)
 *  to the select CODE the form stores */
export function messageCode(v: string, all: [string, string, boolean][]): string {
  if (!v) return "";
  const byCode = all.find((m) => m[0] === v);
  if (byCode) return byCode[0];
  const byLabel = all.find((m) => m[1] === v);
  return byLabel ? byLabel[0] : v;
}
